import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = "https://backend.dlapakistan.com/api";

function DentalNewsPosts() {
  const navigate = useNavigate();
  const { id } = useParams();
  
  // State for news data
  const [newsTitle, setNewsTitle] = useState("");
  const [newsPosts, setNewsPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Utility function to extract data from different API response structures
  const extractDataFromResponse = (response) => {
    if (!response || !response.data) return [];
    
    const data = response.data;
    
    if (Array.isArray(data)) {
      return data;
    } else if (data.results && Array.isArray(data.results)) {
      return data.results;
    } else if (data.data && Array.isArray(data.data)) {
      return data.data;
    } else if (data.items && Array.isArray(data.items)) {
      return data.items;
    } else {
      console.warn("Unexpected API response structure:", data);
      return [];
    }
  };

  useEffect(() => {
    // Fetch news title and posts
    const fetchNewsData = async () => {
      if (!id) {
        console.error("No ID found in URL params");
        setError("No news item selected");
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        console.log("Fetching news data for ID:", id);
        
        // Fetch news title
        const newsResponse = await axios.get(`${API_BASE_URL}/dental-news/${id}/`);
        console.log("News response:", newsResponse.data);
        setNewsTitle(newsResponse.data.title);
        
        // Fetch posts for this news
        const postsResponse = await axios.get(
          `${API_BASE_URL}/dental-news-posts/by_dental_news/?dental_news_id=${id}`
        );
        console.log("Posts response structure:", postsResponse.data);
        
        // Extract data using utility function
        const postsData = extractDataFromResponse(postsResponse);
        
        // Filter only published posts for public website
        const publishedPosts = postsData.filter(post => post.is_published === true);
        
        // Sort posts by created_at date (oldest first)
        const sortedPosts = [...publishedPosts].sort((a, b) => 
          new Date(a.created_at) - new Date(b.created_at)
        );
        
        setNewsPosts(sortedPosts);
        
      } catch (err) {
        console.error("Error fetching news data:", err);
        console.error("Error details:", err.response?.data || err.message);
        setError("Failed to load news posts. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchNewsData();
  }, [id]);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Invalid date";
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString || "Unknown date";
    }
  };

  // Truncate text function
  const truncateText = (text, maxLength = 300) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Extract plain text from HTML for truncation
  const extractTextFromHTML = (html) => {
    if (!html) return "";
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  return (
    <div className="w-full min-h-screen">
      {/* Main Container */}
      <div className="w-full px-4 mt-52 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
        
        {/* Breadcrumb */}
        <div className="flex flex-wrap items-center text-gray-500 mb-4 md:mb-6 text-sm md:text-base">
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/")}
          >
            Home
          </span>
          <span className="mx-2">»</span>
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/dental/news")}
          >
            Dental News
          </span>
          <span className="mx-2">»</span>
          <span className="text-[#70B444] font-medium truncate max-w-xs md:max-w-md">
            {newsTitle || "Loading..."}
          </span>
        </div>

        {/* Main Content Layout */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 relative pb-8 md:pb-12">
          
          {/* MAIN CONTENT AREA */}
          <div className="flex-1 p-0 lg:p-2 md:lg:p-4">
            
            {/* Title Section */}
            <div className="flex justify-between items-center border-b-2 border-[#70B444] pb-3 md:pb-4 mb-4 md:mb-6">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#70B444] leading-snug">
                {newsTitle || "Loading..."}
              </h1>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="space-y-6 md:space-y-8">
                {[1, 2, 3].map((index) => (
                  <div key={index} className="pb-6 md:pb-8 border-b border-gray-200">
                    <div className="h-8 bg-gray-200 rounded w-3/4 mb-2 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-3 animate-pulse"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded w-4/6 animate-pulse"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Error State */}
            {error && !isLoading && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <p className="text-red-600 mb-3 text-lg">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-3 bg-[#70B444] text-white rounded-lg hover:bg-green-600 transition-colors text-base font-medium"
                >
                  Retry
                </button>
              </div>
            )}

            {/* No Posts State */}
            {!isLoading && !error && newsPosts.length === 0 && (
              <div className="text-center py-12 bg-gray-100 border border-gray-300 rounded-lg">
                <p className="text-gray-500 text-lg mb-2">No posts available for this news category.</p>
                <p className="text-gray-400 text-base">Check back later for updates.</p>
              </div>
            )}

            {/* News Posts */}
            {!isLoading && !error && newsPosts.length > 0 && (
              <div className="space-y-6 md:space-y-8">
                {newsPosts.map((post, index) => (
                  <div key={post.id || index} className="pb-6 md:pb-8 border-b border-gray-200 last:border-b-0">
                    <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-[#70B444] mb-2 cursor-pointer hover:underline font-semibold">
                      {post.title}
                    </h2>
                    <p className="text-xs sm:text-sm text-gray-500 mb-3 md:mb-4">
                      Posted on {formatDate(post.created_at)} by DLA Office
                    </p>
                    
                    {/* Show truncated plain text preview */}
                    <div className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-5">
                      {post.description ? (
                        <div className="preview-text">
                          {truncateText(extractTextFromHTML(post.description), 400)}
                        </div>
                      ) : (
                        <p>No content available.</p>
                      )}
                    </div>
                    
                    {/* "Read More" link */}
                    <button
                      onClick={() => navigate(`/dental/news/post/${post.id}`)}
                      className="text-sm md:text-base text-[#70B444] hover:underline font-medium inline-block"
                    >
                      Read More →
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom CSS */}
      <style jsx>{`
        .preview-text {
          font-size: 1.125rem;
          line-height: 1.75;
          color: #374151;
        }
        
        .preview-text p {
          margin-bottom: 1rem;
        }
        
        .full-html-content * { 
          background-color: transparent !important; 
          border: none !important; 
          margin-bottom: 1rem;
          text-align: left;
        }
        
        .full-html-content p {
          font-size: 1.125rem;
          line-height: 1.75;
          color: #374151;
          text-align: left;
        }
        
        .full-html-content h1, 
        .full-html-content h2, 
        .full-html-content h3, 
        .full-html-content h4, 
        .full-html-content h5, 
        .full-html-content h6 {
          font-weight: bold;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
          color: #111827;
          text-align: left;
        }
        
        .full-html-content ul { 
          list-style-type: disc; 
          padding-left: 1.5rem; 
          margin-bottom: 1rem;
          text-align: left;
        }
        
        .full-html-content ol { 
          list-style-type: decimal; 
          padding-left: 1.5rem; 
          margin-bottom: 1rem;
          text-align: left;
        }
        
        .full-html-content a {
          color: #059669;
          text-decoration: underline;
        }
        
        .full-html-content a:hover {
          color: #047857;
        }
        
        .full-html-content img {
          max-width: 100%;
          height: auto;
          margin: 1rem 0;
        }
      `}</style>
    </div>
  );
}

export default DentalNewsPosts;